import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getToken } from "firebase/app-check";
import { apiFetch } from "./apiFetch";
import { appCheck } from "./firebase";

interface Headline {
  title: string;
  source: string;
  url: string;
  publishedAt: string;
  description: string | null;
}

interface SearchResponse {
  headlines: Headline[];
  returned: number;
  total: number;
  message?: string;
  rateLimit: { remaining: number };
}

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function WebsiteSearch() {
  const [title, setTitle] = useState("");
  const [results, setResults] = useState<Headline[]>([]);
  const [total, setTotal] = useState(0);
  const [message, setMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      params.set("title", title.trim());

      let appCheckToken = "";
      try {
        const tokenResponse = await getToken(appCheck, false);
        appCheckToken = tokenResponse.token;
      } catch (err) {
        // Token fetch failed — request will be rejected by backend
      }

      const response = await apiFetch(
        `/api/website-search?${params.toString()}`,
        {
          headers: { "X-Firebase-AppCheck": appCheckToken },
        },
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Search failed (${response.status})`);
      }

      const data: SearchResponse = await response.json();
      setResults(data.headlines);
      setTotal(data.total);
      setMessage(data.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setResults([]);
      setTotal(0);
      setMessage(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card
            className="border-0 shadow-lg"
            style={{
              background: "rgba(255, 255, 255, 0.85)",
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
            }}
          >
            <Card.Header
              className="border-0 text-center pt-4"
              style={{ background: "transparent" }}
            >
              <h2 className="fw-bold mb-1 text-dark">Search Headlines</h2>
              <p className="mb-0 text-muted">
                Search the latest 3 days of news across thousands of sources (whole-word matching)
              </p>
            </Card.Header>
            <Card.Body className="px-4 pb-4">
              <Form onSubmit={handleSearch}>
                <Row className="g-2 align-items-end">
                  <Col>
                    <Form.Group>
                      <Form.Label className="small mb-1 text-dark">Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. tariffs, AI, climate"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs="auto">
                    <Button
                      type="submit"
                      variant="warning"
                      className="fw-bold px-4"
                      disabled={loading || !title.trim()}
                    >
                      {loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Search"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>

              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}

              {hasSearched && !loading && !error && (
                <div className="mt-4">
                  {results.length === 0 ? (
                    <p className="text-center opacity-75 mb-0">
                      No results found.
                    </p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted">
                          Showing {results.length} of{" "}
                          {total.toLocaleString()} results in the past 3 days.
                        </small>
                        {message && (
                          <Link
                            to="/pricing"
                            className="small fw-bold"
                            style={{ textDecoration: "none", color: "#764ba2" }}
                          >
                            Get full API access
                          </Link>
                        )}
                      </div>

                      <div
                        style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {results.map((item, i) => (
                          <a
                            key={i}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-block text-decoration-none"
                            style={{
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <div
                              className="d-flex align-items-start gap-2 p-3 rounded"
                              style={{
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f8f9fa")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "transparent")
                              }
                            >
                              <div className="flex-grow-1" style={{ minWidth: 0, overflow: "hidden" }}>
                                <div
                                  className="fw-semibold"
                                  style={{
                                    fontSize: "0.95rem",
                                    lineHeight: 1.4,
                                    color: "#1a1a2e",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.title}
                                </div>
                                {item.description && (
                                  <div
                                    className="mt-1"
                                    style={{
                                      fontSize: "0.82rem",
                                      lineHeight: 1.5,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      color: "#6c757d",
                                    }}
                                  >
                                    {stripHtml(item.description)}
                                  </div>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <Badge
                                  pill
                                  style={{
                                    fontSize: "0.72rem",
                                    background: "#667eea",
                                    color: "#fff",
                                  }}
                                  className="text-nowrap"
                                >
                                  {item.source}
                                </Badge>
                                <small
                                  className="text-nowrap text-muted"
                                  style={{ fontSize: "0.72rem" }}
                                >
                                  {timeAgo(item.publishedAt)}
                                </small>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>

                      {message && (
                        <div className="text-center mt-3">
                          <small className="text-muted">{message}</small>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
